// TRIAGE-01 (IMP-01 / Q8) — queue navigation over the EFFECTIVE filtered
// set:
//
//   Space        → next data point WITH pending findings
//   Shift+Space  → next UNSEEN data point
//
// "Effective" (Q8, decided 2026-06-11): both jumps walk the SAME ordered
// image set every widget reduces to — `useImagesQuery` capture order
// intersected with `useFilteredDataPoints().visibleImageIds` (the live
// 7-axis global filter) — and "pending" is derived from the DATASET-WIDE
// `useEffectiveFindingSet` (server-narrowed by the committed AI-class/
// confidence filter), so the queue can never disagree with what the
// List/Tiles/Viewer display.
//
// Completeness guard (IMP-01 edge): the dataset-wide findings query is
// page-capped; when `items.length < total` the pending map would be
// partial and the jump could silently skip images whose findings sit
// beyond the cap. In that case `goToNextPendingImage` falls back to a
// PLAIN next-image step rather than lying.
//
// Clamp at the end — no wrap-around (ANA-R3-NAV-01 OQ2).
import { useCallback, useMemo } from "react";
import type { ImageRef } from "@aeriosiq/types";
import { useImagesQuery } from "../queries/useImagesQuery.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useEffectiveFindingSet } from "../useEffectiveFindingSet.js";
import { useFilteredDataPoints } from "../useFilteredDataPoints.js";

export interface TriageQueue {
  /**
   * Activate the next data point (after the active one, filtered order)
   * that still has ≥1 pending finding in the effective set. Falls back
   * to a plain next-image step when the dataset-wide findings set is
   * incomplete (page cap). Returns false when there is nothing to go to.
   */
  goToNextPendingImage: () => boolean;
  /**
   * Activate the next data point the calling user has not seen yet
   * (`DataPoint.seen === false`, per-user `image_review_states`).
   */
  goToNextUnseenImage: () => boolean;
}

/** Pure walk helper — exported for unit tests. */
export function findNextImageId(
  orderedImageIds: readonly string[],
  activeImageId: string | null,
  predicate: (imageId: string) => boolean,
): string | null {
  const startIdx =
    activeImageId === null ? -1 : orderedImageIds.indexOf(activeImageId);
  for (let i = startIdx + 1; i < orderedImageIds.length; i += 1) {
    const id = orderedImageIds[i];
    if (id !== undefined && predicate(id)) return id;
  }
  return null; // clamp — no wrap
}

export function useTriageQueue(dataAcquisitionId: string): TriageQueue {
  const imagesQuery = useImagesQuery(dataAcquisitionId);
  const filtered = useFilteredDataPoints(dataAcquisitionId);
  // Dataset-wide effective findings (no image scope) — the same query
  // that feeds the selection reconciler, server-narrowed by the
  // committed AI-class filter.
  const datasetFindingSet = useEffectiveFindingSet(dataAcquisitionId);

  // Same composition as useDataPointNavigation / the Filmstrip so the
  // queue and the plain arrows never disagree about order/membership.
  const orderedImages = useMemo<ImageRef[]>(() => {
    const allImages = imagesQuery.data?.items ?? [];
    if (filtered.isPending) return allImages;
    return allImages.filter((img) => filtered.visibleImageIds.has(img.imageId));
  }, [imagesQuery.data, filtered.visibleImageIds, filtered.isPending]);

  const orderedImageIds = useMemo(
    () => orderedImages.map((img) => img.imageId),
    [orderedImages],
  );

  // imageId → pending-finding count over the effective set.
  const pendingCountByImageId = useMemo(() => {
    const map = new Map<string, number>();
    for (const f of datasetFindingSet.findings) {
      if (f.status !== "pending") continue;
      map.set(f.imageId, (map.get(f.imageId) ?? 0) + 1);
    }
    return map;
  }, [datasetFindingSet.findings]);

  // Completeness: the dataset-wide query is page-capped; a partial set
  // must not drive skip decisions.
  const findingsData = datasetFindingSet.query.data;
  const pendingMapComplete =
    findingsData !== undefined &&
    findingsData.items.length >= findingsData.total;

  // imageId → seen (per-user) from the filtered data points.
  const seenByImageId = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const dp of filtered.dataPoints) {
      if (dp.visImageId) map.set(dp.visImageId, dp.seen);
    }
    return map;
  }, [filtered.dataPoints]);

  const goToNextPendingImage = useCallback((): boolean => {
    const { activeImageId, setActive } = useImageActivationStore.getState();
    const target = pendingMapComplete
      ? findNextImageId(
          orderedImageIds,
          activeImageId,
          (id) => (pendingCountByImageId.get(id) ?? 0) > 0,
        )
      : // Incomplete pending map → degrade to a plain next step.
        findNextImageId(orderedImageIds, activeImageId, () => true);
    if (target === null) return false;
    setActive(target);
    return true;
  }, [orderedImageIds, pendingCountByImageId, pendingMapComplete]);

  const goToNextUnseenImage = useCallback((): boolean => {
    const { activeImageId, setActive } = useImageActivationStore.getState();
    const target = findNextImageId(
      orderedImageIds,
      activeImageId,
      (id) => seenByImageId.get(id) === false,
    );
    if (target === null) return false;
    setActive(target);
    return true;
  }, [orderedImageIds, seenByImageId]);

  return { goToNextPendingImage, goToNextUnseenImage };
}
